import { useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import Select from "react-select";
import {
  mdiTrayArrowUp,
  mdiTrayArrowDown,
  mdiRefresh,
  mdiCheckCircleOutline,
  mdiTrashCanOutline,
  mdiClose,
} from "@mdi/js";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../../context_providers/modal/loader";
import {
  UploadBulkOrderCsvApi,
  GetBulkOrderBatchDetailApi,
  GetBulkOrderBatchesApi,
  ResolveBulkOrderRowApi,
  SetBulkOrderMergeGroupDecisionApi,
  CreateBulkOrdersApi,
} from "../../../../service/repos/order_repo";

type Counts = {
  ready: number;
  conflict: number;
  error: number;
  created: number;
  pending_merge_groups?: number;
};

interface ShippingAddress {
  branch_name?: string;
  line1?: string;
  city?: string;
  state?: string;
  pincode?: number | string;
}

interface BulkRow {
  id: string;
  batch_id: string;
  row_number: number;
  order_reference?: string;
  raw: any;
  raw_rows?: any[];
  status: "Ready" | "Conflict" | "Error" | "Created";
  messages: string[];
  customer_id?: string;
  customer_name?: string;
  spoc_name?: string;
  order_type?: string;
  created_order_id?: string;
  merge_group_id?: string;
  merge_suggested?: boolean;
  merge_decision?: "pending" | "merge" | "separate";
  candidates?: {
    uid: string;
    name: string;
    gst_number?: string;
    shipping_addresses?: ShippingAddress[];
    dealer_code?: string | number;
    customer_group?: string;
  }[];
  delivery_options?: (ShippingAddress & { customer_id: string })[];
}

interface BulkBatch {
  id: string;
  file_name?: string;
  total_rows: number;
  counts: Counts;
  created_at?: string;
}

const STATUS_STYLES: Record<string, string> = {
  Ready: "bg-green-100 text-green-700",
  Conflict: "bg-amber-100 text-amber-700",
  Error: "bg-red-100 text-red-700",
  Created: "bg-blue-100 text-blue-700",
};

const RAW_KEY_BY_HEADER: Record<string, string> = {
  "Customer Number": "customer_number",
  "GST Number": "gst_number",
  "Order Type": "order_type",
  "Sales Type": "sales_type",
  "Product Item Code": "product_item_code",
  "Product Quantity": "product_quantity",
  "Product Price Per Unit (excl. GST)": "product_price_per_unit",
  "Services Item Code": "services_item_code",
  "Services Quantity": "services_quantity",
  "Services Price Per Unit (excl. GST)": "services_price_per_unit",
  "Raw Materials Item Code": "raw_materials_item_code",
  "Raw Materials Quantity": "raw_materials_quantity",
  "Raw Materials Price Per Unit (excl. GST)": "raw_materials_price_per_unit",
};

const csvCell = (v: any) => {
  const s = v === undefined || v === null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const addressLine = (a: ShippingAddress) =>
  [a.branch_name, a.line1, a.city, a.state, a.pincode]
    .filter(Boolean)
    .join(", ");

const shippingSummary = (list?: ShippingAddress[]) => {
  const lines = (list || []).map(addressLine).filter(Boolean);
  if (lines.length === 0) return "No delivery address";
  return lines.join(" | ");
};

const resolveSelectStyles = {
  control: (base: any) => ({ ...base, minHeight: 28, fontSize: 12 }),
  menu: (base: any) => ({
    ...base,
    minWidth: 320,
    maxWidth: "min(560px, 90vw)",
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (base: any) => ({
    ...base,
    fontSize: 12,
    whiteSpace: "normal" as const,
    overflowWrap: "anywhere" as const,
  }),
};

const itemSummary = (rawRows: any[]) => {
  const parts: string[] = [];
  for (const raw of rawRows || []) {
    if (raw?.product_item_code)
      parts.push(`P: ${raw.product_item_code}×${raw.product_quantity || "?"}`);
    if (raw?.services_item_code)
      parts.push(
        `S: ${raw.services_item_code}×${raw.services_quantity || "?"}`,
      );
    if (raw?.raw_materials_item_code)
      parts.push(
        `RM: ${raw.raw_materials_item_code}×${raw.raw_materials_quantity || "?"}`,
      );
  }
  return parts.join(", ") || "—";
};

const BulkCreateOrderPage = () => {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);

  const [batch, setBatch] = useState<BulkBatch | null>(null);
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [recent, setRecent] = useState<BulkBatch[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("");

  const loadRecent = async () => {
    try {
      const res = await GetBulkOrderBatchesApi({ page: 1, count: 20 });
      if (res?.success) setRecent(res.data?.batches || []);
    } catch {
      /* non-blocking */
    }
  };

  useEffect(() => {
    loadRecent();
  }, []);

  const loadBatch = async (batchId: string) => {
    setLoading(true);
    try {
      const res = await GetBulkOrderBatchDetailApi(batchId);
      if (res?.success) {
        setBatch(res.data.batch);
        setRows(res.data.rows || []);
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to load",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const TEMPLATE_HEADERS = [
    "Customer Number",
    "GST Number",
    "Order Type",
    "Sales Type",
    "Product Item Code",
    "Product Quantity",
    "Product Price Per Unit (excl. GST)",
    "Services Item Code",
    "Services Quantity",
    "Services Price Per Unit (excl. GST)",
    "Raw Materials Item Code",
    "Raw Materials Quantity",
    "Raw Materials Price Per Unit (excl. GST)",
  ];

  const handleDownloadTemplate = () => {
    const csv = TEMPLATE_HEADERS.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "bulk-order-template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    if (!file) {
      showToast({ type: ToastType.error, text: "Please choose a CSV file" });
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await UploadBulkOrderCsvApi(form);
      if (res?.success) {
        showToast({
          type: ToastType.success,
          text: `Uploaded ${res.data.total_rows} rows`,
        });
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        await loadBatch(res.data.batch_id);
        loadRecent();
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Upload failed",
        });
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e?.message || "Upload failed" });
    } finally {
      setUploading(false);
    }
  };

  const resolveRow = async (
    row: BulkRow,
    body: { customer_id?: string; delivery_customer_id?: string },
  ) => {
    setLoading(true);
    try {
      const res = await ResolveBulkOrderRowApi(row.batch_id, row.id, body);
      if (res?.success) {
        await loadBatch(row.batch_id);
        loadRecent();
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Could not resolve row",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMergeDecision = async (
    row: BulkRow,
    decision: "merge" | "separate",
  ) => {
    if (!row.merge_group_id) return;
    setLoading(true);
    try {
      const res = await SetBulkOrderMergeGroupDecisionApi(
        row.batch_id,
        row.merge_group_id,
        decision,
      );
      if (res?.success) {
        await loadBatch(row.batch_id);
        loadRecent();
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Could not set merge decision",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!batch) return;
    setCreating(true);
    try {
      const res = await CreateBulkOrdersApi(batch.id);
      if (res?.success) {
        const { created_count, failed } = res.data;
        showToast({
          type: ToastType.success,
          text: `Created ${created_count} order(s)${
            failed?.length ? `, ${failed.length} failed` : ""
          }`,
        });
        await loadBatch(batch.id);
        loadRecent();
      } else {
        showToast({
          type: ToastType.error,
          text: res?.error || "Create failed",
        });
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e?.message || "Create failed" });
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadFailedRows = () => {
    const errorRows = rows.filter((r) => r.status === "Error");
    if (errorRows.length === 0) {
      showToast({ type: ToastType.error, text: "No failed rows to download" });
      return;
    }
    // Batches uploaded before the excl.-GST template hold GST-inclusive prices.
    const hasLegacyPrices = errorRows.some((row) =>
      (row.raw_rows && row.raw_rows.length ? row.raw_rows : [row.raw]).some(
        (raw: any) => raw && raw.prices_excl_gst !== true,
      ),
    );
    if (hasLegacyPrices) {
      showToast({
        type: ToastType.warning,
        text: "These rows were uploaded with GST-inclusive prices. Re-enter prices excluding GST before re-uploading.",
      });
    }
    const lines = [TEMPLATE_HEADERS.join(",")];
    for (const row of errorRows) {
      const rawRows =
        row.raw_rows && row.raw_rows.length ? row.raw_rows : [row.raw];
      for (const raw of rawRows) {
        lines.push(
          TEMPLATE_HEADERS.map((h) =>
            csvCell(raw?.[RAW_KEY_BY_HEADER[h]]),
          ).join(","),
        );
      }
    }
    const csv = lines.join("\n") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const base = (batch?.file_name || "bulk-order").replace(/\.csv$/i, "");
    link.setAttribute("download", `${base}-failed-rows.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Clears only the freshly-selected input file (not a loaded/recent batch).
  const handleClearSelectedFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClearFile = () => {
    setFile(null);
    setBatch(null);
    setRows([]);
    setStatusFilter("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const counts = batch?.counts;
  const visibleRows = statusFilter
    ? rows.filter((r) => r.status === statusFilter)
    : rows;

  // Rows merged into one order (merge_decision "merge") count once towards
  // the number of orders Create Orders will actually produce, not once per
  // CSV row — mirrors the grouping CreateBulkOrdersController applies.
  const ordersToCreateCount = (() => {
    const seenGroups = new Set<string>();
    let count = 0;
    for (const r of rows) {
      if (r.status !== "Ready") continue;
      if (
        r.merge_suggested &&
        r.merge_decision === "merge" &&
        r.merge_group_id
      ) {
        if (seenGroups.has(r.merge_group_id)) continue;
        seenGroups.add(r.merge_group_id);
      }
      count++;
    }
    return count;
  })();

  return (
    <div className="flex flex-col gap-4 min-h-screen p-4 md:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-xl md:text-2xl font-bold border-l-4 border-myPrimaryColor pl-2">
          Bulk Create Orders
        </h1>
        <button
          type="button"
          onClick={handleDownloadTemplate}
          className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs md:text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <Icon path={mdiTrayArrowDown} size={0.8} />
          Download template
        </button>
      </div>

      {/* Upload card */}
      <div className="rounded-lg border bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-xs md:text-sm"
          />
          <button
            onClick={handleUpload}
            disabled={uploading || !file}
            className="inline-flex items-center gap-2 rounded-md bg-myPrimaryColor px-4 py-2 text-xs md:text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
          >
            <Icon path={mdiTrayArrowUp} size={0.8} />
            {uploading ? "Uploading…" : "Upload CSV"}
          </button>
          {file && (
            <button
              type="button"
              onClick={handleClearSelectedFile}
              className="inline-flex items-center gap-2 rounded-md border border-red-300 px-3 py-2 text-xs md:text-sm font-medium text-red-600 hover:bg-red-50"
            >
              <Icon path={mdiTrashCanOutline} size={0.8} />
              Clear file
            </button>
          )}
        </div>
        <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs md:text-sm text-amber-800">
          Note: Enter the base price per unit <b>excluding GST</b>. GST is added
          automatically from each item&apos;s tax slab (e.g. ₹15,999 at 5% GST
          is saved as ₹16,798.95). Use the latest template — files with the old
          price columns are rejected.
        </p>
        <p className="mt-2 text-xs text-gray-500">
          The SPOC Number is matched to a customer's contact (SPOC) number.
          Shared numbers are flagged as conflicts for manual selection.
          Warehouse is assigned later.
        </p>
      </div>

      {/* Recent uploads */}
      {/* {recent.length > 0 && (
        <div className="rounded-lg border bg-white p-3">
          <p className="mb-2 text-xs font-semibold text-gray-600">
            Recent uploads
          </p>
          <div className="flex flex-wrap gap-2">
            {recent.map((b) => (
              <button
                key={b.id}
                onClick={() => loadBatch(b.id)}
                className={`rounded border px-2 py-1 text-xs hover:bg-gray-50 ${
                  batch?.id === b.id ? "border-myPrimaryColor" : ""
                }`}
                title={b.file_name}
              >
                {b.file_name || b.id} · {b.total_rows} rows
              </button>
            ))}
          </div>
        </div>
      )} */}

      {/* Batch review */}
      {batch && (
        <div className="rounded-lg border bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 font-semibold text-gray-700">
                {batch.file_name || batch.id}
                <button
                  type="button"
                  onClick={handleClearFile}
                  title="Clear selected file"
                  className="ml-0.5 rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                >
                  <Icon path={mdiClose} size={0.6} />
                </button>
              </span>
              {counts && (
                <>
                  <StatusChip
                    label="Ready"
                    value={counts.ready}
                    onClick={() => setStatusFilter("Ready")}
                    active={statusFilter === "Ready"}
                  />
                  <StatusChip
                    label="Conflict"
                    value={counts.conflict}
                    onClick={() => setStatusFilter("Conflict")}
                    active={statusFilter === "Conflict"}
                  />
                  <StatusChip
                    label="Error"
                    value={counts.error}
                    onClick={() => setStatusFilter("Error")}
                    active={statusFilter === "Error"}
                  />
                  <StatusChip
                    label="Created"
                    value={counts.created}
                    onClick={() => setStatusFilter("Created")}
                    active={statusFilter === "Created"}
                  />
                  {statusFilter && (
                    <button
                      onClick={() => setStatusFilter("")}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Clear filter
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadBatch(batch.id)}
                className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs hover:bg-gray-50"
              >
                <Icon path={mdiRefresh} size={0.7} />
                Refresh
              </button>
              {!!counts?.error && (
                <button
                  onClick={handleDownloadFailedRows}
                  className="inline-flex items-center gap-1 rounded-md border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                >
                  <Icon path={mdiTrayArrowDown} size={0.7} />
                  Download failed rows ({counts.error})
                </button>
              )}
              <button
                onClick={handleCreate}
                disabled={
                  creating || !counts?.ready || !!counts?.pending_merge_groups
                }
                title={
                  counts?.pending_merge_groups
                    ? `Resolve ${counts.pending_merge_groups} merge decision(s) first`
                    : undefined
                }
                className="inline-flex items-center gap-2 rounded-md bg-myPrimaryColor px-4 py-2 text-xs font-medium text-white hover:opacity-90 disabled:opacity-60"
              >
                <Icon path={mdiCheckCircleOutline} size={0.8} />
                {creating
                  ? "Creating…"
                  : `Create Orders${counts?.ready ? ` (${ordersToCreateCount})` : ""}`}
              </button>
            </div>
          </div>
          {!!counts?.pending_merge_groups && (
            <p className="mb-3 text-xs font-medium text-amber-700">
              {counts.pending_merge_groups} group(s) of rows look like the same
              customer/order — choose Merge or Keep Separate for each below
              before creating orders.
            </p>
          )}

          <div className="overflow-auto border rounded">
            <table className="w-full text-xs divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left">#</th>
                  <th className="px-2 py-2 text-left">Order Ref</th>
                  <th className="px-2 py-2 text-left">SPOC Number</th>
                  <th className="px-2 py-2 text-left">SPOC Name</th>
                  <th className="px-2 py-2 text-left">Customer Name</th>
                  <th className="px-2 py-2 text-left">Customer ID</th>
                  <th className="px-2 py-2 text-left">Order Type</th>
                  <th className="px-2 py-2 text-left">Items</th>
                  <th className="px-2 py-2 text-left">Status</th>
                  <th className="px-2 py-2 text-left">Details / Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleRows.map((row) => (
                  <tr key={row.id} className="align-top">
                    <td className="px-2 py-2">{row.row_number}</td>
                    <td className="px-2 py-2">
                      {row.order_reference || "—"}
                      {row.merge_suggested && (
                        <span
                          title="Rows sharing the same customer, order type and sales type"
                          className={`ml-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                            row.merge_decision === "merge"
                              ? "bg-green-100 text-green-700"
                              : row.merge_decision === "separate"
                                ? "bg-gray-100 text-gray-600"
                                : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          Group
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2">{row.raw?.customer_number}</td>
                    <td className="px-2 py-2">{row.spoc_name || "—"}</td>
                    <td className="px-2 py-2">{row.customer_name || "—"}</td>
                    <td className="px-2 py-2">{row.customer_id || "—"}</td>
                    <td className="px-2 py-2">
                      {row.order_type || row.raw?.order_type || "—"}
                    </td>
                    <td className="px-2 py-2">
                      {itemSummary(row.raw_rows || [row.raw])}
                    </td>
                    <td className="px-2 py-2">
                      <span
                        className={`rounded px-2 py-0.5 text-[11px] font-medium ${
                          STATUS_STYLES[row.status] || "bg-gray-100"
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <RowAction
                        row={row}
                        rows={rows}
                        onResolve={resolveRow}
                        onMergeDecision={handleMergeDecision}
                      />
                    </td>
                  </tr>
                ))}
                {visibleRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-2 py-6 text-center text-gray-400"
                    >
                      No rows
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(loading || uploading || creating) && <LoadingWidget />}
    </div>
  );
};

const StatusChip = ({
  label,
  value,
  onClick,
  active,
}: {
  label: string;
  value: number;
  onClick: () => void;
  active: boolean;
}) => (
  <button
    onClick={onClick}
    className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
      STATUS_STYLES[label] || "bg-gray-100"
    } ${active ? "ring-2 ring-offset-1 ring-gray-400" : ""}`}
  >
    {label}: {value}
  </button>
);

const RowAction = ({
  row,
  rows,
  onResolve,
  onMergeDecision,
}: {
  row: BulkRow;
  rows: BulkRow[];
  onResolve: (
    row: BulkRow,
    body: { customer_id?: string; delivery_customer_id?: string },
  ) => void;
  onMergeDecision: (row: BulkRow, decision: "merge" | "separate") => void;
}) => {
  if (row.status === "Created") {
    return (
      <span className="text-blue-600">{row.created_order_id || "Created"}</span>
    );
  }

  if (row.status === "Ready" && row.merge_suggested) {
    const otherRowNumbers = rows
      .filter((r) => r.merge_group_id === row.merge_group_id && r.id !== row.id)
      .map((r) => r.row_number)
      .sort((a, b) => a - b);

    if (row.merge_decision === "pending") {
      return (
        <div className="flex flex-col gap-1">
          <span className="text-amber-700">
            Same customer/order as row{otherRowNumbers.length > 1 ? "s" : ""}{" "}
            {otherRowNumbers.join(", ")} — merge into one order?
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onMergeDecision(row, "merge")}
              className="rounded border border-green-300 px-2 py-1 text-[11px] font-medium text-green-700 hover:bg-green-50"
            >
              Merge
            </button>
            <button
              type="button"
              onClick={() => onMergeDecision(row, "separate")}
              className="rounded border px-2 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50"
            >
              Keep Separate
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        <span className="text-green-700">
          {row.merge_decision === "merge"
            ? `Will be merged with row${otherRowNumbers.length > 1 ? "s" : ""} ${otherRowNumbers.join(", ")}`
            : "Kept as a separate order"}
        </span>
        <button
          type="button"
          onClick={() =>
            onMergeDecision(
              row,
              row.merge_decision === "merge" ? "separate" : "merge",
            )
          }
          className="self-start text-[11px] text-blue-600 hover:underline"
        >
          Change decision
        </button>
      </div>
    );
  }

  if (row.status === "Conflict") {
    if (row.candidates && row.candidates.length > 0) {
      return (
        <div className="flex flex-col gap-1">
          <span className="text-amber-700">{row.messages?.[0]}</span>
          <Select<{ value: string; c: any }, false>
            placeholder="Select customer…"
            value={null}
            options={row.candidates.map((c) => ({ value: c.uid, c }))}
            onChange={(o) => o && onResolve(row, { customer_id: o.value })}
            filterOption={(o, input) =>
              `${o.data.c.name} ${o.data.c.gst_number || ""} ${o.data.c.uid} ${shippingSummary(o.data.c.shipping_addresses)}`
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            formatOptionLabel={({ c }) => (
              <div className="flex flex-col">
                <span className="font-medium">{c.name}</span>
                <span className="text-[11px] text-gray-600">
                  {[
                    c.gst_number && `GST ${c.gst_number}`,
                    c.dealer_code && `Dealer ${c.dealer_code}`,
                    c.customer_group,
                    `ID ${c.uid}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                <span className="text-[11px] text-gray-500">
                  Delivery: {shippingSummary(c.shipping_addresses)}
                </span>
              </div>
            )}
            styles={resolveSelectStyles}
            menuPortalTarget={document.body}
            className="w-full max-w-[28rem] text-xs"
          />
        </div>
      );
    }
    if (row.delivery_options && row.delivery_options.length > 0) {
      return (
        <div className="flex flex-col gap-1">
          <span className="text-amber-700">{row.messages?.[0]}</span>
          <Select<{ value: string; label: string }, false>
            placeholder="Select delivery address…"
            value={null}
            options={row.delivery_options.map((d) => ({
              value: d.customer_id,
              label: addressLine(d),
            }))}
            onChange={(o) =>
              o && onResolve(row, { delivery_customer_id: o.value })
            }
            styles={resolveSelectStyles}
            menuPortalTarget={document.body}
            className="w-full max-w-[28rem] text-xs"
          />
        </div>
      );
    }
    return <span className="text-amber-700">{row.messages?.[0]}</span>;
  }

  if (row.status === "Error") {
    return (
      <ul className="list-disc pl-4 text-red-600">
        {(row.messages || []).map((m, i) => (
          <li key={i}>{m}</li>
        ))}
      </ul>
    );
  }

  return <span className="text-green-700">Ready to create</span>;
};

export default BulkCreateOrderPage;
